/**
 * Error Log Viewer Utility
 *
 * Provides access to all error logs from various sources:
 * - localStorage (recent_errors)
 * - Browser console (if available)
 * - Sentry (if configured)
 */

interface ErrorLogEntry {
  message: string;
  stack?: string;
  context?: Record<string, any>;
  timestamp: string;
  source: 'localStorage' | 'console' | 'sentry' | 'logger';
  level?: 'error' | 'warning' | 'info' | 'debug';
}

export class ErrorLogViewer {
  /**
   * Get all errors from localStorage
   */
  static getLocalStorageErrors(): ErrorLogEntry[] {
    try {
      const stored = localStorage.getItem('recent_errors');
      if (!stored) return [];

      const errors = JSON.parse(stored);
      return errors.map((e: any) => ({
        ...e,
        source: 'localStorage' as const,
        level: 'error' as const,
      }));
    } catch (error) {
      console.error('Failed to read errors from localStorage', error);
      return [];
    }
  }

  /**
   * Get all errors from console (if available)
   */
  static getConsoleErrors(): ErrorLogEntry[] {
    // Note: Browser console errors are not directly accessible via JavaScript
    // This would need to be implemented via a console interceptor
    return [];
  }

  /**
   * Get all error logs (combined from all sources)
   */
  static getAllErrors(): ErrorLogEntry[] {
    const errors: ErrorLogEntry[] = [];

    // Get localStorage errors
    errors.push(...this.getLocalStorageErrors());

    // Sort by timestamp (newest first)
    return errors.sort(
      (a, b) =>
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );
  }

  /**
   * Clear all error logs
   */
  static clearAllErrors(): void {
    try {
      localStorage.removeItem('recent_errors');
    } catch (error) {
      console.error('Failed to clear errors from localStorage', error);
    }
  }

  /**
   * Export errors as JSON
   */
  static exportErrors(): string {
    const errors = this.getAllErrors();
    return JSON.stringify(errors, null, 2);
  }

  /**
   * Get error summary statistics
   */
  static getErrorSummary(): {
    total: number;
    byLevel: Record<string, number>;
    recent: number; // Errors in last hour
    oldest: string | null;
    newest: string | null;
  } {
    const errors = this.getAllErrors();
    const now = Date.now();
    const oneHourAgo = now - 60 * 60 * 1000;

    const byLevel: Record<string, number> = {};
    let recent = 0;
    let oldest: string | null = null;
    let newest: string | null = null;

    errors.forEach(error => {
      // Count by level
      const level = error.level || 'error';
      byLevel[level] = (byLevel[level] || 0) + 1;

      // Count recent
      const timestamp = new Date(error.timestamp).getTime();
      if (timestamp > oneHourAgo) {
        recent++;
      }

      // Track oldest/newest
      if (!oldest || timestamp < new Date(oldest).getTime()) {
        oldest = error.timestamp;
      }
      if (!newest || timestamp > new Date(newest).getTime()) {
        newest = error.timestamp;
      }
    });

    return {
      total: errors.length,
      byLevel,
      recent,
      oldest,
      newest,
    };
  }

  /**
   * Filter errors by search term
   */
  static filterErrors(
    errors: ErrorLogEntry[],
    searchTerm: string
  ): ErrorLogEntry[] {
    if (!searchTerm) return errors;

    const term = searchTerm.toLowerCase();
    return errors.filter(
      error =>
        error.message.toLowerCase().includes(term) ||
        error.stack?.toLowerCase().includes(term) ||
        JSON.stringify(error.context || {})
          .toLowerCase()
          .includes(term)
    );
  }
}
